import { useContext, useEffect, useState } from 'react';

import { AppWindowStyleContext, ChatWindowOpenStyle, POPUP_WINDOW } from '../../context/app-window-style';
import { useCall } from '../../context/call-context';
import checkAvailability from '../../data/check-availability';
import type { AvailableAgent } from '../../types';
import enableSdkButtons from '../../util/enable-sdk-buttons';
import hideWidget from '../../util/hide-widget';
import isSdkMode from '../../util/is-sdk-mode';

const initialState = {
  isPopupOpen: false,
  items: [],
  loadingProductExpert: false,
  message: '',
  newMessageCount: 0,
  videoChadEnabled: false
};

export const usePopupChat = () => {
  const { setIsPopupOpen, isPopupOpen: windowPopupOpen } = useContext(AppWindowStyleContext);
  const [widgetState, setWidgetState] = useState<typeof initialState>(initialState);
  const [chatEnabled, setChatEnabled] = useState<boolean>(false);
  const [availableAgent, setAvailableAgent] = useState<AvailableAgent>();
  const [noAvailableAgents, setNoAvailableAgents] = useState<number>(0);
  const [isAgentsOnline, setAgentsOnline] = useState<boolean>(false);
  const { product, chatWindowOpenStyle, productType, agent } = useCall();
  const { isPopupOpen } = widgetState;

  //Open the chat window by default in window view
  useEffect(() => {
    if (chatWindowOpenStyle === ChatWindowOpenStyle.WINDOW && window.name === POPUP_WINDOW) {
      const urlParams = new URLSearchParams(window.location.search);
      const popupWindow = urlParams.get(POPUP_WINDOW);
      if (popupWindow === 'true') {
        setWidgetState((prevState) => ({
          ...prevState,
          isPopupOpen: true
        }));
      }
    }
  }, []);

  useEffect(() => {
    if (noAvailableAgents > 0) {
      enableSdkButtons({ bookAppointment: true, directCall: true, showSplash: true, startCall: true });
    } else {
      enableSdkButtons({ bookAppointment: true, directCall: false, showSplash: false, startCall: false });
    }
  }, [noAvailableAgents]);

  //change the base page popup state
  useEffect(() => {
    if (chatWindowOpenStyle === ChatWindowOpenStyle.WINDOW && window.name !== POPUP_WINDOW) {
      setWidgetState((prevState) => ({
        ...prevState,
        isPopupOpen: windowPopupOpen
      }));
    }
  }, [windowPopupOpen]);

  let intervalId: NodeJS.Timer | number = 0;
  let pollCount = 1;
  const startPolling = (baseUrl: string, team?: string, timeout = 3000) => {
    if (intervalId === 0) {
      intervalId = setInterval(() => pollForAgents(baseUrl, team), timeout);
    }
  };

  const stopPolling = (onsuccess?: () => void) => {
    clearInterval(intervalId);
    intervalId = 0;
    if (onsuccess) {
      onsuccess();
    }
  };

  window.checkAbility = async (baseUrl: string, team: string, category: string, agentUsername: string) =>
    await checkAvailability(team, category, agentUsername);

  const pollForAgents = async (baseUrl: string, team?: string) => {
    const response = await checkAvailability(team, product?.type || productType, agent);

    pollCount += 1;
    if (pollCount === 100) {
      console.debug({ message: 'check availability stop', pollCount });
      stopPolling();
      setNoAvailableAgents(0);
      setAvailableAgent(undefined);
      setChatEnabled(false);
      setAgentsOnline(false);
      return;
    }
    if (pollCount % 40 === 0) {
      stopPolling();
      const timeout = (pollCount / 20 + 1) * 3000;
      console.debug({ message: 'check availability timer changes', pollCount, timeout });
      startPolling(baseUrl, team, timeout);
    }
    const { availableAgent, noOfAvailableAgents, isAgentsOnline } = response;
    setNoAvailableAgents(noOfAvailableAgents);
    setAvailableAgent(availableAgent);
    setChatEnabled(!!isAgentsOnline && noOfAvailableAgents > 0);
    setAgentsOnline(!!isAgentsOnline);
    return availableAgent;
  };

  window.pollForAgents = pollForAgents;

  const togglePopup = () => {
    if (widgetState.isPopupOpen && isSdkMode()) {
      hideWidget();
    }
    setWidgetState((prevState) => ({
      ...prevState,
      isPopupOpen: !prevState.isPopupOpen,
      newMessageCount: 0
    }));
  };

  //Open the window form base page in window view
  useEffect(() => {
    if (chatWindowOpenStyle === ChatWindowOpenStyle.WINDOW && window.name !== POPUP_WINDOW) {
      setIsPopupOpen(widgetState.isPopupOpen);
    }
  }, [widgetState.isPopupOpen]);

  return {
    availableAgent,
    chatEnabled,
    isAgentsOnline,
    isPopupOpen,
    noAvailableAgents,
    pollForAgents,
    startPolling,
    stopPolling,
    togglePopup
  };
};
